import { useLazyQuery, useMutation } from "@apollo/client"
import { Button, Spinner, VStack } from "@chakra-ui/react"
import {
  BlocksEditContainer,
  useEditorBlocks,
  useEditorContent,
  useEditorDispatch
} from "@pathwright/blocks-engine/"
import BlocksPublishDiff from "@pathwright/blocks/src/editor/ContentPublishDiff"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import USER_QUERY from "@pathwright/web/src/modules/user/graphql/user-query"
import { Suspense, useEffect, useState } from "react"
import { getGraphQLEndpoint } from "../pathwright/PathwrightClient"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import PUBLISH_BLOCKS_CONTENT from "./graphql/publish-blocks-content-mutation"
import { updateCachedPathItemBlocks } from "./utils"

type PublishButtonProps = {
  loading?: boolean
  onPublish?: (data: any) => void
  label?: string
}

const PublishButton = ({ loading, onPublish, label }: PublishButtonProps) => {
  const [publishCompleted, setPublishCompleted] = useState(false)

  const { handlePublishContent } = useEditorDispatch()
  const {
    error,
    syncing,
    isPublished,
    lastPublished,
    lastModified,
    publishing
  } = useEditorContent()

  const { blocks } = useEditorBlocks()

  const { tc } = useTranslate()

  useEffect(() => {
    if (isPublished && !publishing) {
      setPublishCompleted(true)
    }
  }, [lastPublished])

  useEffect(() => {
    if (publishCompleted) {
      if (onPublish) {
        onPublish({
          lastPublishedDateTime: lastPublished,
          lastModifiedDateTime: lastModified
        })
      }
      setPublishCompleted(false)
    }
  }, [publishCompleted])

  return (
    <Button
      cursor="pointer"
      title={
        !blocks?.length
          ? tc("No blocks found. Add at least one block in order to publish.")
          : ""
      }
      onClick={() => {
        if (isPublished) return
        if (handlePublishContent) handlePublishContent()
      }}
      isLoading={publishing || loading}
      isDisabled={
        loading ||
        syncing ||
        publishing ||
        isPublished ||
        !!error ||
        !blocks?.length
      }
      loadingText={publishing ? tc("Saving...") : tc("Loading...")}
      colorScheme="brand"
      display={publishing || loading ? undefined : "block"}
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {label || (isPublished ? tc("Saved") : tc("Save Blocks Content"))}
    </Button>
  )
}

type BlocksPublishProps = {
  contextKey: string
  item: any
  onPublish?: () => void
  label?: string
}

const BlocksPublish = ({
  onPublish,
  label,
  me
}: {
  onPublish: (data: any) => void
  me: any
  label?: string
}) => {
  const { changes } = useEditorContent()

  return (
    <VStack spacing={4} align="center">
      <BlocksPublishDiff me={me} changes={changes} />
      <PublishButton onPublish={onPublish} label={label} />
    </VStack>
  )
}

const BlocksPublishContainer = ({
  contextKey,
  item,
  onPublish,
  label
}: BlocksPublishProps) => {
  const [updateBlocksContent] = useMutation(PUBLISH_BLOCKS_CONTENT)

  const [getUser] = useLazyQuery(USER_QUERY)

  const context = usePathwrightContext()
  if (!context.school || !context.me) return null

  const graphQLEndpoint = `${getGraphQLEndpoint()}blocks/graphql?school_id=${
    context.school.id
  }`

  const handlePublish = async ({
    lastPublishedDateTime,
    lastModifiedDateTime
  }: {
    lastPublishedDateTime: string
    lastModifiedDateTime: string
  }) => {
    if (!item?.content_id) return

    // Convert to ISO string for Django
    const lastPublished = lastPublishedDateTime
      ? new Date(lastPublishedDateTime).toISOString()
      : null
    const contentLastModified = lastModifiedDateTime
      ? new Date(lastModifiedDateTime).toISOString()
      : new Date().toISOString()

    if (!item?.content_id) return

    const payload = {
      id: item.content_id,
      stepId: item.id,
      contentLastModified,
      lastPublished
    }

    const isPublished = lastPublished && lastPublished > contentLastModified

    await updateBlocksContent({
      variables: payload
    })

    updateCachedPathItemBlocks(item, {
      id: item.content_id,
      type: "blocks",
      content_last_modified: contentLastModified,
      last_published: lastPublished
    })

    // Trigger event for other components to update, namely the Blocks Content Editor
    window.dispatchEvent(
      new CustomEvent("blocksPublished", {
        detail: { contextKey }
      })
    )

    // Prevent false positive unwanted updates from PublishButton
    if (onPublish && isPublished) onPublish()
  }

  const handleGetUserByID = async (userID: number) => {
    try {
      return await getUser({ variables: { id: userID } })
    } catch (error) {
      console.error("Error fetching user:", error)
    }
  }

  return (
    <Suspense fallback={<Spinner />}>
      <BlocksEditContainer
        userID={context.me.id!}
        accountID={context.school.id}
        contextKey={contextKey}
        mode="EDIT"
        graphQLEndpoint={graphQLEndpoint}
        renderLoading={() => <PublishButton loading />}
        getUserByID={handleGetUserByID}
      >
        <BlocksPublish
          onPublish={handlePublish}
          label={label}
          me={context.me}
        />
      </BlocksEditContainer>
    </Suspense>
  )
}

export default BlocksPublishContainer
